import { EVEN_MONTHLY_FAMILY_KEYS } from '../../../services/schedulePreview';
import type { AlignedCollectionTargets } from '../../../services/collectionPeriods';
import { deriveEvenMonthlySchedule, type EvenMonthlyDerivation } from '../../../services/evenMonthlyTerm';
import { isValidPaymentSelection } from '../../../types/schedule.types';
import type {
    SchedulePreviewParams,
    CollectionTarget,
    SchedulePreview,
    ScheduleMeta,
    ScheduleRow,
    PaymentSelection,
    CollectionRouting,
} from '../../../types/schedule.types';
import type { Product, SchedulingMethod } from '@/features/products/types/products.types';
import type { ConfiguredService, ConfiguredAddOn } from '@/features/service-plans/types/services.types';
import { todayDateKey } from '../../../utils/dateKey';
import { unavailableReasonText } from './copy';
import type { PaymentPreviewBlockReason } from '../../../utils/stepGate';

export const EMPTY_ALIGNMENT: AlignedCollectionTargets = Object.freeze({
    targets: [] as CollectionTarget[],
    numInstalments: undefined,
});

// Field-by-field: JSON.stringify is key-order fragile.
export const schedulePreviewParamsEqual = (
    a: SchedulePreviewParams,
    b: SchedulePreviewParams,
): boolean => {
    if (
        a.amount !== b.amount ||
        a.hasDeposit !== b.hasDeposit ||
        a.depositAmount !== b.depositAmount ||
        a.numInstalments !== b.numInstalments ||
        a.frequency !== b.frequency ||
        a.dealerCollectedDepositAmount !== b.dealerCollectedDepositAmount
    ) {
        return false;
    }
    const aTargets = a.collectionTargets;
    const bTargets = b.collectionTargets;
    if (aTargets === undefined || bTargets === undefined) {
        return aTargets === bTargets;
    }
    if (aTargets.length !== bTargets.length) {
        return false;
    }
    return aTargets.every((target, index) => {
        const other = bTargets[index];
        return (
            target.amount === other.amount &&
            target.byPeriod === other.byPeriod &&
            target.byDate === other.byDate
        );
    });
};

// Rows are typed but never validated at runtime: a corrupted persisted entry must
// read as unequal here rather than throw where its fields are dereferenced.
const isScheduleRow = (value: unknown): value is ScheduleRow =>
    typeof value === 'object' && value !== null;

// Field-by-field, as above: the persisted snapshot is a JSON round-trip of a response
// object, so neither reference identity nor JSON.stringify can compare the two.
export const schedulePreviewEqual = (a: SchedulePreview, b: SchedulePreview): boolean => {
    if (a.numPayments !== b.numPayments || a.schedule.length !== b.schedule.length) {
        return false;
    }
    if (
        a.meta.total_repayable !== b.meta.total_repayable ||
        a.meta.total_interest !== b.meta.total_interest ||
        a.meta.total_principal !== b.meta.total_principal ||
        a.meta.total_fees !== b.meta.total_fees ||
        a.meta.balloon !== b.meta.balloon
    ) {
        return false;
    }
    return a.schedule.every((row, index) => {
        const other = b.schedule[index];
        if (!isScheduleRow(row) || !isScheduleRow(other)) {
            return false;
        }
        return (
            row.number === other.number &&
            row.date === other.date &&
            row.payment === other.payment &&
            row.principal === other.principal &&
            row.interest === other.interest &&
            row.balance === other.balance
        );
    });
};

// Receipt fields change nothing in `params` or `snapshot`, so without this an edited
// reference/invoice/date reads as already-recorded and is never re-committed.
export const collectionRoutingEqual = (
    a: CollectionRouting | undefined,
    b: CollectionRouting | undefined,
): boolean => {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    if (a.depositCollection !== b.depositCollection || a.fullPaymentCollection !== b.fullPaymentCollection) {
        return false;
    }
    const aPayment = a.dealerPayment;
    const bPayment = b.dealerPayment;
    if (aPayment === undefined || bPayment === undefined) {
        return aPayment === bPayment;
    }
    return (
        aPayment.amountMinor === bPayment.amountMinor &&
        aPayment.reference === bPayment.reference &&
        aPayment.invoiceNumber === bPayment.invoiceNumber &&
        aPayment.collectedAt === bPayment.collectedAt
    );
};

// Lives in utils/collectionRouting.ts so quoteSlice can read it without a value
// edge into this transport-touching tree (a store/module import cycle).
export { dealerCollectedAxis } from '../../../utils/collectionRouting';
export type { CollectionRoutingAxis } from '../../../utils/collectionRouting';

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

const META_TOTAL_KEYS: readonly (keyof ScheduleMeta)[] = [
    'total_repayable',
    'total_interest',
    'total_principal',
    'total_fees',
    'balloon',
];

// Only the three fields the contract's probe run measured (§6): principal, interest and
// balance are derived there, so requiring them could refuse a response elemis really sends.
const isCommittableRow = (row: unknown): boolean =>
    isScheduleRow(row) &&
    isFiniteNumber(row.number) &&
    typeof row.date === 'string' &&
    isFiniteNumber(row.payment);

// A commit freezes this snapshot into the quote and on into the agreement, so a settled
// response missing the totals or rows the contract promises is a failed preview, not a schedule.
export const isCommittableSnapshot = (snapshot: SchedulePreview): boolean => {
    const meta: unknown = snapshot.meta;
    if (typeof meta !== 'object' || meta === null || Array.isArray(meta)) {
        return false;
    }
    const totals = meta as Partial<Record<keyof ScheduleMeta, unknown>>;
    return (
        isFiniteNumber(snapshot.numPayments) &&
        META_TOTAL_KEYS.every((key) => isFiniteNumber(totals[key])) &&
        snapshot.schedule.every(isCommittableRow)
    );
};

export interface CommittableSelectionInputs {
    isReadOnly: boolean;
    selectedMethod: SchedulingMethod | null;
    params: SchedulePreviewParams | null;
    data: SchedulePreview | undefined;
    error: Error | null;
    isFetching: boolean;
    isPlaceholderData: boolean;
    anchor: string;
    recordedLeadMonths: number | undefined;
    collectionRouting: CollectionRouting | undefined;
    /** A resumed dealer-collected routing must block the commit while this
     *  holds, never be silently dropped from it. */
    isDealerConfigUnresolved: boolean;
    /** Same contract: an unrecorded or still-debouncing receipt blocks the
     *  commit rather than writing a selection the screen does not show. */
    isDealerReceiptIncomplete: boolean;
}

// Single source for what the auto-commit effect writes: the gate and the selection
// shape live together so PaymentStep's effect and the step gate cannot disagree.
export const buildCommittableSelection = (
    inputs: CommittableSelectionInputs,
): PaymentSelection | null => {
    const {
        isReadOnly,
        selectedMethod,
        params,
        data,
        error,
        isFetching,
        isPlaceholderData,
        anchor,
        recordedLeadMonths,
        collectionRouting,
        isDealerConfigUnresolved,
        isDealerReceiptIncomplete,
    } = inputs;
    if (isReadOnly || !selectedMethod || !params || !data || error !== null) return null;
    if (isFetching || isPlaceholderData || isDealerConfigUnresolved) return null;
    if (isDealerReceiptIncomplete) return null;
    const selection = {
        methodKey: selectedMethod.key,
        params,
        snapshot: data,
        scheduleAnchorDate: anchor,
        ...(recordedLeadMonths !== undefined ? { collectionLeadMonths: recordedLeadMonths } : {}),
        ...(collectionRouting !== undefined ? { collectionRouting } : {}),
    };
    // Fail closed: a malformed preview response must never commit, or the step
    // gate can never re-open.
    return isValidPaymentSelection(selection) && isCommittableSnapshot(data) ? selection : null;
};

export type LeadRejectionKind = 'non-integer' | 'out-of-range';

export const resolveLeadRejection = (
    months: number | undefined,
    bounds: { min: number; max: number } | null,
    isItemBasedFamily: boolean,
): LeadRejectionKind | null => {
    if (bounds === null || !isItemBasedFamily || months === undefined) {
        return null;
    }
    if (!Number.isInteger(months)) {
        return 'non-integer';
    }
    if (months < bounds.min || months > bounds.max) {
        return 'out-of-range';
    }
    return null;
};

export const EMPTY_REASONS: Readonly<Record<string, string>> = Object.freeze({});

export const buildUnavailableReasons = (
    methods: SchedulingMethod[],
    derivation: EvenMonthlyDerivation,
): Readonly<Record<string, string>> => {
    if (derivation.kind !== 'not-offerable') {
        return EMPTY_REASONS;
    }
    const reasons: Record<string, string> = {};
    for (const method of methods) {
        if (!EVEN_MONTHLY_FAMILY_KEYS.has(method.key)) {
            continue;
        }
        reasons[method.key] = unavailableReasonText(method.label, derivation);
    }
    return Object.keys(reasons).length === 0 ? EMPTY_REASONS : reasons;
};

// `null` when every configured method is unavailable — nothing is preselected, so each
// method keeps its own reason and the preview pane shows its choose-a-method empty state.
export const resolveDefaultMethodKey = (
    product: Product | null | undefined,
    configuredServices: ConfiguredService[],
    configuredAddOns: ConfiguredAddOn[],
): string | null => {
    const methods = product?.schedulingMethods ?? [];
    if (methods.length === 0) {
        return null;
    }
    const evenMonthly = deriveEvenMonthlySchedule({
        services: configuredServices,
        addOns: configuredAddOns,
        anchorDateKey: todayDateKey(),
        leadMonths: product?.collectionLeadMonths,
        minInstalments: product?.minCollectionInstalments,
        defaultInstalments: product?.defaultCollectionInstalments,
    });
    const reasons = buildUnavailableReasons(methods, evenMonthly);
    const offerable = methods.filter((method) => reasons[method.key] === undefined);
    if (offerable.length === 0) {
        return null;
    }
    // Prefer the product's configured default_schedule when it is offerable; otherwise fall back
    // to the first offerable method (today's behaviour). A stale/unknown key is ignored here.
    const preferred = product?.defaultSchedule;
    if (preferred !== undefined && offerable.some((method) => method.key === preferred)) {
        return preferred;
    }
    return offerable[0].key;
};

// False when nothing else is available: the payment step then offers no way to clear the
// unavailability, so gating on it would refuse Next with no on-screen remedy.
export const isUnavailableWithAlternative = (
    methods: SchedulingMethod[],
    selectedMethodKey: string | null,
    reasons: Readonly<Record<string, string>>,
): boolean => {
    if (selectedMethodKey === null || reasons[selectedMethodKey] === undefined) {
        return false;
    }
    return methods.some((method) => reasons[method.key] === undefined);
};

// False when the product carries no methods at all: the selector then offers nothing to
// choose, and a resumed quote's product blob routinely carries none.
export const isSelectedMethodUnoffered = (
    methods: SchedulingMethod[],
    selectedMethodKey: string | null,
): boolean =>
    selectedMethodKey !== null &&
    methods.length > 0 &&
    !methods.some((method) => method.key === selectedMethodKey);

// The case isUnavailableWithAlternative deliberately excludes — no OTHER offered method is
// available — and mutually exclusive with it. A null key is this state, not an escape from it.
export const isAllMethodsUnavailable = (
    methods: SchedulingMethod[],
    selectedMethodKey: string | null,
    reasons: Readonly<Record<string, string>>,
): boolean =>
    methods.length > 0 &&
    (selectedMethodKey === null || reasons[selectedMethodKey] !== undefined) &&
    methods.every((method) => reasons[method.key] !== undefined);

export interface PaymentPreviewBlockInputs {
    isReadOnly: boolean;
    isLiveScheduleRecorded: boolean;
    methods: SchedulingMethod[];
    selectedMethodKey: string | null;
    hasSelectedMethod: boolean;
    unavailableReasons: Readonly<Record<string, string>>;
    hasDepositCeilingRejection: boolean;
    hasLeadRejection: boolean;
    hasUncommittablePreview: boolean;
    /** Dealer collection chosen but the receipt cannot yet be recorded. */
    hasIncompleteDealerReceipt: boolean;
    /** A priced service falls due before it could be collected (stale/past-dated quote). */
    hasPastServiceDate: boolean;
}

// Single source for what reportPaymentPreviewBlocked carries (#1643): each branch is
// independently testable here rather than as an inline expression in PaymentStep.
export const derivePaymentPreviewBlockReason = (
    inputs: PaymentPreviewBlockInputs,
): PaymentPreviewBlockReason | null => {
    const {
        isReadOnly,
        isLiveScheduleRecorded,
        methods,
        selectedMethodKey,
        hasSelectedMethod,
        unavailableReasons,
        hasDepositCeilingRejection,
        hasLeadRejection,
        hasUncommittablePreview,
        hasIncompleteDealerReceipt,
        hasPastServiceDate,
    } = inputs;

    if (isReadOnly || isLiveScheduleRecorded) {
        return null;
    }
    if (isSelectedMethodUnoffered(methods, selectedMethodKey)) {
        return 'method-unoffered';
    }
    // Ahead of the no-selection carve-out below: a fresh all-unavailable quote has no selected
    // method precisely because none is selectable, and that state still needs its own reason.
    if (isAllMethodsUnavailable(methods, selectedMethodKey, unavailableReasons)) {
        return 'all-methods-unavailable';
    }
    if (!hasSelectedMethod) {
        return null;
    }
    // A stale/past-dated service is a fundamental block with its own remedy (re-date on
    // Configure), so it outranks the generic 'unsettled' lump below.
    if (hasPastServiceDate) {
        return 'past-service-date';
    }
    if (
        hasDepositCeilingRejection ||
        hasLeadRejection ||
        hasIncompleteDealerReceipt ||
        isUnavailableWithAlternative(methods, selectedMethodKey, unavailableReasons) ||
        hasUncommittablePreview
    ) {
        return 'unsettled';
    }
    return null;
};
